import React, { useState, useEffect, useRef } from 'react';
import '../../App.css';
import Navbar from '../navbar';
import TopNavbar from '../topnavbar';
import Footer from '../footer';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';

const MaternityTracker = () => {
  
  const [maternityData, setMaternityData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const navigate = useNavigate();
  const importInputRef = useRef(null);
  const [deliveryType, setDeliveryType] = useState('Normal'); 
  const [employeeStatus, setEmployeeStatus] = useState('Active');
  const [searchName, setSearchName] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [selectedImport, setSelectedImport] = useState(null);
  const [importDetails, setImportDetails] = useState([]);
  const [showImportDetailsModal, setShowImportDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewResult, setPreviewResult] = useState({ ready: [], duplicates: [], notFound: [] });
  const [pendingRows, setPendingRows] = useState([]);
  const [previewTab, setPreviewTab] = useState('ready');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Expected Delivery Date filters
  const [filterExpectedYear, setFilterExpectedYear] = useState('');
  const [filterExpectedMonth, setFilterExpectedMonth] = useState('');
  
  // Request Date filters
  const [filterRequestYear, setFilterRequestYear] = useState('');
  const [filterRequestMonth, setFilterRequestMonth] = useState('');
  
  // Dropdown filters
  const [filterMaternityType, setFilterMaternityType] = useState('');
  const [filterEmployeeStatus, setFilterEmployeeStatus] = useState('');
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editForm, setEditForm] = useState({
    TransactionNum: '',
    ExpectedDeliveryDate: '',
    ConfineStartDate: '',
    ConfineEndDate: '',
    SSNotificationDate: ''
  });


  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  useEffect(() => {
  fetchMaternityData();
  fetchImportHistory(); // 👈 ADD THIS
}, []);

  useEffect(() => {
    applyFilters();
  }, [searchName, filterExpectedYear, filterExpectedMonth, filterRequestYear, filterRequestMonth, filterMaternityType, filterEmployeeStatus, maternityData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredData]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleDownloadTemplate = () => {
    const templateData = [{
      'UID': '',
      'Employee Name': '',
      'SSS Number': '',
      'Maternity Notification Request Date': '',
      'SSS Notification Date': '',
      'Transaction No.': '',
      'Expected Date of Delivery': '',
      'Maternity Leave Start': '',
      'Maternity Leave End': ''
    }];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const colWidths = [
      { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 38 },
      { wch: 25 }, { wch: 18 }, { wch: 28 }, { wch: 22 }, { wch: 20 }
    ];
    worksheet['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Maternity Template');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, 'MaternityTracker_Template.xlsx');
  };

  // STEP 1: Read file and call preview endpoint
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreviewing(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      const mapped = rows.map((row, index) => ({
        rowNum: index + 2,
        EmpId: row['UID'] || '',
        EmployeeName: row['Employee Name'] || '',
        SSS: row['SSS Number'] || '',
        TransactionNum: row['Transaction No.'] || '',
        SSNotificationDate: row['SSS Notification Date'] || '',
        RequestDate: row['Maternity Notification Request Date'] || '',
        ExpectedDeliveryDate: row['Expected Date of Delivery'] || '',
        ConfineStartDate: row['Maternity Leave Start'] || '',
        ConfineEndDate: row['Maternity Leave End'] || ''
      }));

      // Call the preview endpoint (no DB inserts yet)
      const previewResponse = await fetch('/api/previewMaternityImport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: mapped })
      });

      if (!previewResponse.ok) throw new Error('Preview failed');

      const result = await previewResponse.json();

      setPendingRows(mapped);
      setPreviewResult(result);
      setPreviewTab('ready');
      setShowPreviewModal(true);

    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to read file for preview.', { position: 'top-right', autoClose: 3000 });
    } finally {
      setPreviewing(false);
      e.target.value = '';
    }
  };

  // STEP 2: Confirm import — only sends ready rows
  const handleConfirmImport = async () => {
    if (previewResult.ready.length === 0) {
      toast.warn('No new records to import.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setImporting(true);
    setShowPreviewModal(false);

    try {
      const [trackerResponse, tableResponse] = await Promise.all([
       fetch('/api/importMaternityTracker', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rows: previewResult.ready,
            importedBy: sessionStorage.getItem('employeeId'), // or employeeId (check your key)
            facility: sessionStorage.getItem('facility'),
            fileName: 'MaternityTracker_Import.xlsx'
          })
        }),
       
      ]);

      const trackerResult = await trackerResponse.json();

      if (trackerResult.success > 0) {
        toast.success(`${trackerResult.success} record(s) uploaded successfully!`, {
          position: 'top-right', autoClose: 3000
        });
      }
      if (trackerResult.duplicates?.length > 0) {
        toast.warn(`${trackerResult.duplicates.length} record(s) already exist in the system.`, {
          position: 'top-right', autoClose: 3000
        });
      }
      if (trackerResult.notFound?.length > 0) {
        toast.error(`${trackerResult.notFound.length} employee(s) not found in database.`, {
          position: 'top-right', autoClose: 3000
        });
      }
      if (trackerResult.success === 0 && !trackerResult.duplicates?.length && !trackerResult.notFound?.length) {
        toast.error('No records were imported.', { position: 'top-right', autoClose: 3000 });
      }

      fetchMaternityData();
      fetchImportHistory();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import file.', { position: 'top-right', autoClose: 3000 });
    } finally {
      setImporting(false);
    }
  };

  const fetchMaternityData = async () => {
    try {
      const facility = sessionStorage.getItem('facility');
      const response = await fetch('/api/getMaternityTracker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facility })
      });
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const jsonResponse = await response.json();
      const data = jsonResponse.result || [];
      setMaternityData(data);
      setFilteredData(data);
    } catch (error) {
      console.error('Error fetching maternity data:', error);
      toast.error('Failed to load maternity data.', { position: 'top-right', autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

const fetchImportHistory = async () => {
  try {
    const res = await fetch('/api/getImportHistory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        facility: sessionStorage.getItem('facility')
      })
    });

    const json = await res.json();

    console.log('IMPORT HISTORY RESPONSE:', json);

    
    setImportHistory(Array.isArray(json) ? json : json.result || []);
  } catch (err) {
    console.error(err);
  }
};

const fetchImportDetails = async (importID) => {
  setDetailsLoading(true);
  try {
    const res = await fetch('/api/getImportDetails', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ImportID: importID })
    });

    const json = await res.json();
    setImportDetails(json || []);
  } catch (err) {
    console.error(err);
  } finally {
    setDetailsLoading(false);
  }
};
const handleViewImportDetails = (importItem) => {
  setSelectedImport(importItem);
  fetchImportDetails(importItem.ImportID);
  setShowImportDetailsModal(true);
};

const handleDeleteImport = async (importID) => {
  try {
    const deletedBy = sessionStorage.getItem('employeeId');

    const res = await fetch('/api/deleteImport', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ImportID: importID,
        DeletedBy: deletedBy
      })
    });

    const json = await res.json();

    if (res.ok) {
      toast.success('Import history deleted successfully!', {
        position: 'top-right',
        autoClose: 3000
      });
      fetchImportHistory();
      fetchMaternityData();
    } else {
      toast.error(json.error || 'Failed to delete import history.', {
        position: 'top-right',
        autoClose: 3000
      });
    }
  } catch (error) {
    console.error(error);
    toast.error('Failed to delete import history.', {
      position: 'top-right',
      autoClose: 3000
    });
  }
};

  const getYearMonth = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    } catch (e) {
      return '';
    }
  };

  const applyFilters = () => {
    let data = [...maternityData];

    if (searchName.trim() !== '') {
      const searchLower = searchName.toLowerCase();
      const searchNoDash = searchName.replace(/-/g, '');
      data = data.filter(item =>
        (item.EmployeeName && item.EmployeeName.toLowerCase().includes(searchLower)) ||
        (item.EmpId && item.EmpId.toString().toLowerCase().includes(searchLower)) ||
        (item.SSS && item.SSS.toString().replace(/-/g, '').includes(searchNoDash)) ||
        (item.TransactionNum && item.TransactionNum.toString().toLowerCase().replace(/-/g, '').includes(searchLower.replace(/-/g, '')))
      );
    }

    if (filterExpectedYear !== '' || filterExpectedMonth !== '') {
      data = data.filter(item => {
        const itemYearMonth = getYearMonth(item.ExpectedDeliveryDate);
        if (!itemYearMonth) return false;
        const [itemYear, itemMonth] = itemYearMonth.split('-');
        if (filterExpectedYear !== '' && filterExpectedMonth !== '') return itemYear === filterExpectedYear && itemMonth === filterExpectedMonth;
        if (filterExpectedYear !== '') return itemYear === filterExpectedYear;
        if (filterExpectedMonth !== '') return itemMonth === filterExpectedMonth;
        return true;
      });
    }

    if (filterRequestYear !== '' || filterRequestMonth !== '') {
      data = data.filter(item => {
        const itemYearMonth = getYearMonth(item.RequestDate);
        if (!itemYearMonth) return false;
        const [itemYear, itemMonth] = itemYearMonth.split('-');
        if (filterRequestYear !== '' && filterRequestMonth !== '') return itemYear === filterRequestYear && itemMonth === filterRequestMonth;
        if (filterRequestYear !== '') return itemYear === filterRequestYear;
        if (filterRequestMonth !== '') return itemMonth === filterRequestMonth;
        return true;
      });
    }

    if (filterMaternityType !== '') {
      data = data.filter(item => item.MaternityType === filterMaternityType);
    }

    if (filterEmployeeStatus !== '') {
      data = data.filter(item => item.EmployeeStatus === filterEmployeeStatus);
    }

    setFilteredData(data);
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toISOString().slice(0, 10);
    } catch (e) {
      return '';
    }
  };

  const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch (e) {
    return '—';
  }
};
  

  const parseDate = (val) => {
    if (!val || val === '' || val === 'For Update' || val === 'NOTIFIED BY EMPLOYEE') return null;

    // If it's a number (Excel serial date)
    if (typeof val === 'number') {
      const date = new Date(Math.round((val - 25569) * 86400 * 1000));
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }

    // If it's a string
    if (typeof val === 'string') {
      val = val.trim();

      // MM/DD/YYYY format
      if (val.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
        const parts = val.split('/');
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }

      // Already YYYY-MM-DD format
      if (val.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return val;
      }

      // MM-DD-YYYY format (like SSS numbers e.g. 06-4203265-6 should NOT be parsed)
      if (val.match(/^\d{1,2}-\d{1,2}-\d{4}$/)) {
        const parts = val.split('-');
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }

    return null;
  };

  const addDays = (dateStr, days) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]) + days);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleClearFilters = () => {
    setSearchName('');
    setFilterExpectedYear('');
    setFilterExpectedMonth('');
    setFilterRequestYear('');
    setFilterRequestMonth('');
    setFilterMaternityType('');
    setFilterEmployeeStatus('');
  };

  const handleDownloadExcel = () => {
    const exportData = filteredData.map(item => ({
      'UID': item.EmpId || '',
      'Employee Name': item.EmployeeName || '',
      'SSS Number': item.SSS || '',
      'Maternity Notification Request Date': item.RequestDate ? formatDateOnly(item.RequestDate) : '',
      'SSS Notification Date': item.SSNotificationDate ? formatDateOnly(item.SSNotificationDate) : 'For Update',
      'Transaction No.': item.TransactionNum || 'For Update',
      'Expected Date of Delivery': item.ExpectedDeliveryDate ? formatDateOnly(item.ExpectedDeliveryDate) : 'For Update',
      'Maternity Leave Start': item.ConfineStartDate ? formatDateOnly(item.ConfineStartDate) : 'For update',
      'Maternity Leave End': item.ConfinementEnd ? formatDateOnly(item.ConfinementEnd) : 'For update',
      'No. of Days': item.TotalDays ?? ''
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const headers = Object.keys(exportData[0] || {});
    const colWidths = headers.map(header => {
      const maxDataLen = exportData.reduce((max, row) => {
        const cellVal = row[header] ? row[header].toString().length : 0;
        return Math.max(max, cellVal);
      }, 0);
      return { wch: Math.max(header.length, maxDataLen) + 2 };
    });
    worksheet['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Maternity Tracker');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(dataBlob, `MaternityTracker_${formatDateOnly(new Date())}.xlsx`);
  };

  const handleEditClick = (item) => {
    setSavingEdit(false);
    setSelectedItem(item);
    setDeliveryType(item.MaternityType || 'Normal');
    setEmployeeStatus(item.EmployeeStatus || 'Active');
    const defaultStart = (item.ExpectedDeliveryDate && item.ExpectedDeliveryDate !== null)
      ? formatDateOnly(item.ExpectedDeliveryDate)
      : formatDateOnly(item.RequestDate);
    const defaultEnd = (item.ConfinementEnd && item.ConfinementEnd !== null)
      ? formatDateOnly(item.ConfinementEnd)
      : addDays(defaultStart, 104);
    setEditForm({
      TransactionNum: item.TransactionNum || '',
      ExpectedDeliveryDate: formatDateOnly(item.ExpectedDeliveryDate) || '',
      ConfineStartDate: defaultStart,
      ConfineEndDate: defaultEnd,
      SSNotificationDate: formatDateOnly(item.SSNotificationDate) || ''
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    setSavingEdit(true);
    try {
      const updateData = {
        SubmissionID: selectedItem.SubmissionID,
        TransactionNum: editForm.TransactionNum,
        ExpectedDeliveryDate: editForm.ExpectedDeliveryDate,
        ConfineStartDate: editForm.ConfineStartDate,
        ConfineEndDate: editForm.ConfineEndDate,
        SSNotificationDate: editForm.SSNotificationDate || '',
        MaternityType: deliveryType,
        EmployeeStatus: employeeStatus
      };

      const response = await fetch('/api/updateMaternityTracker', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      toast.success('Record updated successfully!');
      setShowEditModal(false);
      fetchMaternityData();
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('An error occurred.');
    } finally {
    setSavingEdit(false);
  }
  };

  // Helper to render the preview tab table
  const renderPreviewTable = (rows, type) => {
    if (rows.length === 0) {
      return (
        <div className="text-center py-3" style={{ color: '#888', fontSize: '13px' }}>
          No records in this category.
        </div>
      );
    }

    return (
      <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
        <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0, minWidth: '1100px' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fc', zIndex: 1 }}>
            <tr>
              <th rowSpan="2" className="align-middle text-center">#</th>
              <th>UID</th>
              <th>Employee Name</th>
              <th>SSS Number</th>
              <th>Maternity NotificationRequest Date</th>
              <th>SSS Notification Date</th>              
              <th>Transaction No.</th>
              <th>Expected Date of Delivery</th>
              <th>Leave Start</th>
              <th>Leave End</th>
              {type !== 'ready' && <th>Issue</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="text-center">{i + 1}</td>
                <td>{row.EmpId || '—'}</td>
                <td>{row.EmployeeName || '—'}</td>
                <td>{row.SSS || '—'}</td>
                <td className="text-center">{row.RequestDate ? formatDate(parseDate(row.RequestDate)) : '—'}</td>
                <td className="text-center">{row.SSNotificationDate ? formatDate(parseDate(row.SSNotificationDate)) : '—'}</td>
                <td className="text-center">{row.TransactionNum || '—'}</td>                  
                <td className="text-center">{row.ExpectedDeliveryDate ? formatDate(parseDate(row.ExpectedDeliveryDate)) : '—'}</td>
                <td className="text-center">{row.ConfineStartDate ? formatDate(parseDate(row.ConfineStartDate)) : '—'}</td>
                <td className="text-center">{row.ConfineEndDate ? formatDate(parseDate(row.ConfineEndDate)) : '—'}</td>
                {type !== 'ready' && (
                  <td style={{ color: type === 'notFound' ? '#e74a3b' : '#d4a017' }}>
                    {type === 'notFound' ? 'Employee ID not found' : 'Already in system'}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const tabStyle = (tab) => ({
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: '600',
    border: 'none',
    borderBottom: previewTab === tab ? '2px solid #4e73df' : '2px solid transparent',
    background: 'none',
    color: previewTab === tab ? '#4e73df' : '#888',
    cursor: 'pointer',
    transition: 'all 0.15s'
  });

  return (
    <div id="wrapper">
      <Navbar />
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <TopNavbar />
          <div className="container-fluid">

            {/* Filters */}
            <div className="card shadow-sm mb-3">
              <div className="card-body py-3 px-3">
                <div className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }}>

                  {/* Search */}
                  <div className="flex-grow-1" style={{ minWidth: 200 }}>
                    <div className="d-flex align-items-center bg-light border rounded px-2" style={{ height: 38 }}>
                      <i className="fas fa-search text-muted mr-2" style={{ fontSize: 11 }}></i>
                      <input
                        type="text"
                        className="border-0 bg-transparent"
                        style={{ outline: "none", height: "100%", width: 0, flexGrow: 1, fontSize: 13 }}
                        placeholder="Search by Name, User ID, SSS, or Transaction #"
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Expected Delivery */}
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: 11, fontWeight: "bold", letterSpacing: "0.06em" }}>Expected Delivery</div>
                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      <input
                        type="text" className="form-control form-control-sm" placeholder="YYYY"
                        maxLength="4" value={filterExpectedYear}
                        onChange={(e) => setFilterExpectedYear(e.target.value)} style={{ width: 70 }}
                      />
                      <select className="form-control form-control-sm" value={filterExpectedMonth} onChange={(e) => setFilterExpectedMonth(e.target.value)}>
                        <option value="">Month</option>
                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Request Date */}
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: 11, fontWeight: "bold", letterSpacing: "0.06em" }}>Request Date</div>
                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      <input
                        type="text" className="form-control form-control-sm" placeholder="YYYY"
                        maxLength="4" value={filterRequestYear}
                        onChange={(e) => setFilterRequestYear(e.target.value)} style={{ width: 70 }}
                      />
                      <select className="form-control form-control-sm" value={filterRequestMonth} onChange={(e) => setFilterRequestMonth(e.target.value)}>
                        <option value="">Month</option>
                        {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Maternity Type */}
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: 11, fontWeight: "bold", letterSpacing: "0.06em" }}>Maternity Type</div>
                    <select className="form-control form-control-sm" value={filterMaternityType} onChange={(e) => setFilterMaternityType(e.target.value)}>
                      <option value="">All Types</option>
                      <option value="Normal">Normal (105 days)</option>
                      <option value="Solo Parent">Solo Parent (120 days)</option>
                      <option value="Miscarriage">Miscarriage (60 days)</option>
                    </select>
                  </div>

                  {/* Employee Status */}
                  <div>
                    <div className="text-muted mb-1" style={{ fontSize: 11, fontWeight: "bold", letterSpacing: "0.06em" }}>Employee Status</div>
                    <select className="form-control form-control-sm" value={filterEmployeeStatus} onChange={(e) => setFilterEmployeeStatus(e.target.value)}>
                      <option value="">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Resigned">Resigned</option>
                      <option value="On Maternity Leave">On Maternity Leave</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>

                  {/* Clear */}
                  <div style={{ alignSelf: "flex-end" }}>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleClearFilters}
                      
                    >
                      Clear Filters
                    </button>
                  </div>

                </div>
              </div>
            </div>


            {/* Table */}
            <div className="card shadow mb-4">
              <div className="card-header py-2 d-flex align-items-center justify-content-between">
                <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#4e73df' }}>Maternity Tracker</span>
                <div className="d-flex" style={{ gap: '12px' }}>
                  <input
                    type="file" accept=".xlsx, .xls" ref={importInputRef}
                    style={{ display: 'none' }} onChange={handleImportExcel}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => setShowImportModal(true)}>
                    <i className="fas fa-file-import mr-1"></i> Upload
                  </button>
                  <button className="btn btn-success btn-sm" onClick={handleDownloadExcel} disabled={filteredData.length === 0}>
                     <i className="fas fa-download mr-1"></i> Download
                  </button>
                  <button
                    className="btn btn-info btn-sm"
                    onClick={() => {
                      fetchImportHistory();
                      setShowHistoryModal(true);
                    }}
                  >
                    <i className="fas fa-history mr-1"></i> History
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover" style={{ fontSize: '13px' }}>
                    <thead style={{ backgroundColor: '#4e73df', color: 'white' }}>
                      <tr>
                        <th 
                        rowSpan="2" 
                        className="align-middle text-center" 
                        style={{ width: '14px', padding: '2px', fontSize: '13px' }}
                        >
                        No.
                        </th>
                        <th rowSpan="2" className="align-middle text-center">UID</th>
                        <th rowSpan="2" className="align-middle text-center">Employee Name</th>
                        <th rowSpan="2" className="align-middle text-center">SSS Number</th>
                        <th rowSpan="2" className="align-middle text-center">Maternity Notification <br />Request Date</th>
                        <th rowSpan="2" className="align-middle text-center">SSS Notification Date</th>
                        <th rowSpan="2" className="align-middle text-center">Transaction No.</th>
                        <th rowSpan="2" className="align-middle text-center">Expected Date<br />of Delivery</th>
                        <th colSpan="3" className="text-center">Maternity Leave</th>
                        <th rowSpan="2" className="align-middle text-center">Action</th>
                      </tr>
                      <tr style={{ backgroundColor: '#4e73df', color: 'white' }}>
                        <th className="text-center">Start</th>
                        <th className="text-center">End</th>
                        <th className="text-center">No. of Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <span className="spinner-border spinner-border-sm mr-2" role="status" />Loading...
                          </td>
                        </tr>
                      ) : paginatedData.length === 0 ? (
                        <tr><td colSpan="11" className="text-center py-4">No records found.</td></tr>
                      ) : (
                        paginatedData.map((item, index) => (
                         <tr 
                            key={index}
                            style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fc' }}
                          >
                                                        <td
                            style={{
                                width: '14px',
                                padding: '2px',
                                fontSize: '11px',
                                textAlign: 'center',
                                verticalAlign: 'middle'
                            }}
                            >
                            {(currentPage - 1) * rowsPerPage + index + 1}
                            </td>
                            <td 
                              className="text-center"
                              style={{ 
                                color: '#4e73df',   // 🔵 blue
                                fontWeight: '600'   // bold-ish
                              }}
                            >
                              {item.EmpId || '—'}
                            </td>
                            <td style={{ fontWeight: '600', color: '#5a5c69', textAlign: 'left' }}>
                              {item.EmployeeName || '—'}
                            </td>
                            <td className="text-center">{item.SSS || '—'}</td>
                            <td className="text-center">{formatDate(item.RequestDate)}</td>
                            <td className="text-center">
                              {item.SSNotificationDate ? formatDate(item.SSNotificationDate)
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.TransactionNum && item.TransactionNum.trim() !== '' ? item.TransactionNum
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.ExpectedDeliveryDate ? formatDate(item.ExpectedDeliveryDate)
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.ConfineStartDate ? formatDate(item.ConfineStartDate)
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.ConfinementEnd ? formatDate(item.ConfinementEnd)
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.TotalDays ? item.TotalDays
                                : <span style={{ backgroundColor: '#ffe0e0', color: '#c0392b', borderRadius: '4px', padding: '2px 8px', fontSize: '11px' }}>For Update</span>}
                            </td>
                            <td className="text-center">
                              {item.TypeOfDelivery !== 'IMPORTED' && (
                                <button
                                  className="btn btn-outline-primary btn-sm mr-2"
                                  onClick={() => navigate('/request', {
                                    state: {
                                      data: {
                                        SubmissionID: item.SubmissionID, Name: item.EmployeeName,
                                        EmpId: item.EmpId, EmailAddress: item.EmailAddress,
                                        TransactionType: item.TransactionType, Status: item.Status,
                                        TurnAround: item.TurnAround,
                                        DateTime: item.RequestDate ? formatDate(item.RequestDate) : '',
                                        TransactionNum: item.TransactionNum, TypeOfDelivery: item.TypeOfDelivery,
                                        PlaceOfConfinement: item.PlaceOfConfinement, BankAccountName: item.BankAccountName,
                                        BankName: item.BankName, BankAccountNumber: item.BankAccountNumber,
                                        facility: sessionStorage.getItem('facility')
                                      },
                                      fromMaternityTracker: true
                                    }
                                  })}
                                  title="View"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                              )}
                              <button className="btn btn-outline-primary btn-sm mr-1" onClick={() => handleEditClick(item)} title="Edit">
                                <i className="fas fa-pencil-alt"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="d-flex align-items-center justify-content-center mt-4" style={{ gap: '20px' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                    <span style={{ fontSize: '14px', fontWeight: '500', minWidth: '100px', textAlign: 'center' }}>Page {currentPage} of {totalPages}</span>
                    <button className="btn btn-primary btn-sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ── Import Instructions Modal ── */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered>
        <Modal.Header>
          <Modal.Title style={{ fontSize: '16px' }}>Upload Maternity Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: '13px' }}>Please use the template below to ensure correct data format before importing.</p>
          <div className="alert alert-info" style={{ fontSize: '12px' }}>
            <strong>Instructions:</strong>
            <ol className="mb-0 mt-1">
              <li>Download the template</li>
              <li>Fill in the data following the column headers exactly</li>
              <li>Dates must be in <strong>MM/DD/YYYY</strong> format</li>
              <li>Save the file and click Import File</li>
            </ol>
          </div>
          <div className="alert alert-warning" style={{ fontSize: '12px' }}>
            <strong>Column Headers:</strong> UID, Employee Name, SSS Number, Maternity Notification Request Date, SSS Notification Date, Transaction No., Expected Date of Delivery, Maternity Leave Start, Maternity Leave End
          </div>
          <div className="d-flex justify-content-center" style={{ gap: '12px', marginTop: '20px' }}>
            <button className="btn btn-success btn-sm" onClick={handleDownloadTemplate}>
              <i className="fas fa-download mr-1"></i> Download Template
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { setShowImportModal(false); importInputRef.current.click(); }}>
              <i className="fas fa-file-import mr-1"></i> Upload File
            </button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowImportModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Import Preview Modal ── */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} centered size="xl">
        <Modal.Header>
          <Modal.Title style={{ fontSize: '16px' }}>
            
            Upload Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>

          {/* Summary Cards */}
          <div className="d-flex" style={{ gap: '10px', marginBottom: '16px' }}>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '8px',
              backgroundColor: '#e8f5e9', border: '1px solid #a5d6a7', textAlign: 'center'
            }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#2e7d32' }}>
                {previewResult.ready.length}
              </div>
              <div style={{ fontSize: '11px', color: '#388e3c', fontWeight: '600', marginTop: '2px' }}>
                 Ready to Upload
              </div>
            </div>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '8px',
              backgroundColor: '#fff8e1', border: '1px solid #ffe082', textAlign: 'center'
            }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#f57f17' }}>
                {previewResult.duplicates.length}
              </div>
              <div style={{ fontSize: '11px', color: '#f9a825', fontWeight: '600', marginTop: '2px' }}>
                 Already in System
              </div>
            </div>
            <div style={{
              flex: 1, padding: '12px', borderRadius: '8px',
              backgroundColor: '#ffebee', border: '1px solid #ef9a9a', textAlign: 'center'
            }}>
              <div style={{ fontSize: '22px', fontWeight: '700', color: '#c62828' }}>
                {previewResult.notFound.length}
              </div>
              <div style={{ fontSize: '11px', color: '#e53935', fontWeight: '600', marginTop: '2px' }}>
                 Employee Not Found
              </div>
            </div>
          </div>

          {/* Info note */}
          {(previewResult.duplicates.length > 0 || previewResult.notFound.length > 0) && (
            <div className="alert alert-info py-2" style={{ fontSize: '12px', marginBottom: '12px' }}>
              <i className="fas fa-info-circle mr-1"></i>
              Only <strong>Ready to Import</strong> records will be saved. Duplicates and not-found rows will be skipped.
            </div>
          )}

          {/* Tabs */}
          <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '12px', display: 'flex', gap: '4px' }}>
            <button style={tabStyle('ready')} onClick={() => setPreviewTab('ready')}>
              Ready ({previewResult.ready.length})
            </button>
            <button style={tabStyle('duplicates')} onClick={() => setPreviewTab('duplicates')}>
              Duplicates ({previewResult.duplicates.length})
            </button>
            <button style={tabStyle('notFound')} onClick={() => setPreviewTab('notFound')}>
              Not Found ({previewResult.notFound.length})
            </button>
          </div>

          {/* Tab Content */}
          {previewTab === 'ready' && renderPreviewTable(previewResult.ready, 'ready')}
          {previewTab === 'duplicates' && renderPreviewTable(previewResult.duplicates, 'duplicate')}
          {previewTab === 'notFound' && renderPreviewTable(previewResult.notFound, 'notFound')}

        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowPreviewModal(false)}>Cancel</Button>
          <Button
            variant="primary" size="sm"
            onClick={handleConfirmImport}
            disabled={previewResult.ready.length === 0}
          >
            <i className="fas fa-check mr-1"></i>
            Confirm Upload ({previewResult.ready.length} records)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header>
          <Modal.Title style={{ fontSize: '16px' }}>Edit — {selectedItem?.EmployeeName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-2">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Transaction Number</label>
            <input type="text" className="form-control form-control-sm" value={editForm.TransactionNum}
              onChange={(e) => setEditForm({ ...editForm, TransactionNum: e.target.value })} />
          </div>
          <div className="form-group mb-2">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>SSS Notification Date</label>
            <input type="date" className="form-control form-control-sm" value={editForm.SSNotificationDate}
              onChange={(e) => setEditForm({ ...editForm, SSNotificationDate: e.target.value })} />
          </div>
          <div className="form-group mb-3">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Delivery Type</label>
            <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
              <button type="button" className={`btn btn-sm ${deliveryType === 'Normal' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => { setDeliveryType('Normal'); setEditForm(prev => ({ ...prev, ConfineEndDate: addDays(prev.ConfineStartDate, 104) })); }}>
                Normal (105 days)
              </button>
              <button type="button" className={`btn btn-sm ${deliveryType === 'Solo Parent' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setDeliveryType('Solo Parent'); setEditForm(prev => ({ ...prev, ConfineEndDate: addDays(prev.ConfineStartDate, 119) })); }}>
                Solo Parent (120 days)
              </button>
              <button type="button" className={`btn btn-sm ${deliveryType === 'Miscarriage' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={() => { setDeliveryType('Miscarriage'); setEditForm(prev => ({ ...prev, ConfineEndDate: addDays(prev.ConfineStartDate, 59) })); }}>
                Miscarriage (60 days)
              </button>
            </div>
          </div>
          <div className="form-group mb-3">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Employee Status</label>
            <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
              <button type="button" className={`btn btn-sm ${employeeStatus === 'Active' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setEmployeeStatus('Active')}>Active</button>
              
              <button type="button" className={`btn btn-sm ${employeeStatus === 'Archived' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setEmployeeStatus('Archived')}>Archived</button>
              <button type="button" className={`btn btn-sm ${employeeStatus === 'Resigned' ? 'btn-danger' : 'btn-outline-danger'}`}
                onClick={() => setEmployeeStatus('Resigned')}>Resigned</button>
            </div>
            <small className="text-muted" style={{ fontSize: '11px' }}>
              "On Maternity Leave" is automatically determined by maternity dates.<br />
              "Archived" is automatically set when the leave period ends, but it can also be set manually.
            </small>
          </div>
          <div className="form-group mb-2">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Expected Delivery Date</label>
            <input type="date" className="form-control form-control-sm" value={editForm.ExpectedDeliveryDate}
              onChange={(e) => {
                const newExpected = e.target.value;
                setDeliveryType('Normal');
                setEditForm({ ...editForm, ExpectedDeliveryDate: newExpected, ConfineStartDate: newExpected, ConfineEndDate: addDays(newExpected, 104) });
              }} />
          </div>
          <div className="form-group mb-2">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Maternity Start Date</label>
            <input type="date" className="form-control form-control-sm" value={editForm.ConfineStartDate}
              onChange={(e) => {
                const newStart = e.target.value;
                let days = 104;
                if (deliveryType === 'Solo Parent') days = 119;
                if (deliveryType === 'Miscarriage') days = 59;
                setEditForm({ ...editForm, ConfineStartDate: newStart, ConfineEndDate: addDays(newStart, days) });
              }} />
          </div>
          <div className="form-group mb-2">
            <label style={{ fontSize: '13px', fontWeight: 'bold' }}>Maternity End Date</label>
            <input type="date" className="form-control form-control-sm" value={editForm.ConfineEndDate}
              onChange={(e) => setEditForm({ ...editForm, ConfineEndDate: e.target.value })} />
            <small className="text-muted" style={{ fontSize: '11px' }}>Auto-calculated based on Delivery Type and Start Date.</small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleEditSave} disabled={savingEdit}>
            {savingEdit ? (
              <>
                <span className="spinner-border spinner-border-sm mr-1" role="status" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} centered size="lg">
        <Modal.Header>
          <Modal.Title style={{ fontSize: '16px' }}>
           
            Upload History
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-sm table-bordered">
             <thead
                style={{
                  background: 'linear-gradient(to right, rgba(78,115,223,0.08), rgba(78,115,223,0.02))'
                }}
              >
                <tr>
                  <th style={{ fontWeight: '500', fontSize: '14px' }}>Uploaded By</th>
                  <th style={{ fontWeight: '500', fontSize: '14px' }}>Date Uploaded</th>
                  <th style={{ fontWeight: '500', fontSize: '14px' }}>File Name</th>
                  <th style={{ width: '100px', fontWeight: '500', fontSize: '14px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center" style={{fontSize: '14px' }}>No import history found.</td>
                  </tr>
                ) : (
                  importHistory.map((item, i) => (
                    <tr key={i}>
                      <td style={{fontSize: '14px' }}>{item.UploadedByName || item.ImportedBy}</td>
                      <td style={{fontSize: '14px' }}>{formatDate(item.ImportDateTime)}</td>
                      <td style={{fontSize: '14px' }}>{item.FileName}</td>
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            const confirmDelete = window.confirm("Are you sure you want to delete this file?");
                            if (confirmDelete) {
                              handleDeleteImport(item.ImportID);
                            }
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>   
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowHistoryModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />

      {/* Previewing overlay */}
      {previewing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem' }} role="status" />
          <p style={{ color: 'white', marginTop: '16px', fontWeight: '500' }}>Checking records, please wait...</p>
        </div>
      )}

      {/* Importing overlay */}
      {importing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="spinner-border text-light" style={{ width: '3rem', height: '3rem' }} role="status" />
          <p style={{ color: 'white', marginTop: '16px', fontWeight: '500' }}>Importing data, please wait...</p>
        </div>
      )}
    </div>
  );
};

export default MaternityTracker;